const DEFAULT_DECIMAL_PLACES = 2;

export function getCurrencyDecimalPlaces(currency: string, locale: string): number {
  try {
    const nf = new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() });
    const opts = nf.resolvedOptions();

    if (typeof opts.maximumFractionDigits === 'number') return opts.maximumFractionDigits;
    return DEFAULT_DECIMAL_PLACES;
  } catch {
    return DEFAULT_DECIMAL_PLACES;
  }
}

function isValidDecimalString(str: string): boolean {
  return /^-?\d+(\.\d+)?$/.test(str);
}

/**
 * Nettoie la saisie brute d'un champ montant : ne garde que chiffres, séparateurs et signe, puis
 * normalise la virgule décimale en point. Retire donc le symbole de devise et les espaces (dont
 * les espaces insécables de la mise en forme `fr-FR`) : sans ça, éditer un montant affiché
 * « 110,00 € » produisait « 110.00 € », illisible par `parsePriceToMinorUnit`. Le signe `-` est
 * conservé pour que `parsePriceToMinorUnit` rejette un montant négatif au lieu d'en inverser le signe.
 */
export function sanitizePriceInput(rawInput: string): string {
  return rawInput.replace(/[^\d.,-]/g, '').replace(',', '.');
}

export function parsePriceToMinorUnit(rawInput: string, currency: string | undefined, locale: string): number | null {
  const normalized = rawInput.replace(',', '.').trim();
  if (!isValidDecimalString(normalized)) return null;

  const floatValue = parseFloat(normalized);
  if (isNaN(floatValue) || floatValue < 0) return null;

  const decimalPlaces = currency ? getCurrencyDecimalPlaces(currency, locale) : DEFAULT_DECIMAL_PLACES;
  const multiplier = Math.pow(10, decimalPlaces);

  return Math.round(floatValue * multiplier);
}

export function formatMinorUnitToRawPrice(
  amountInMinorUnit: number,
  currency: string | undefined,
  locale: string,
): string {
  if (amountInMinorUnit === null || amountInMinorUnit === undefined || isNaN(amountInMinorUnit)) return '';

  const decimalPlaces = currency ? getCurrencyDecimalPlaces(currency, locale) : DEFAULT_DECIMAL_PLACES;
  const divisor = Math.pow(10, decimalPlaces);

  return (amountInMinorUnit / divisor).toFixed(decimalPlaces);
}

export function formatPriceFromMinorUnit(
  amountInMinorUnit: number,
  currency: string,
  locale: string,
  extraDecimalPlaces = 0,
): string {
  if (amountInMinorUnit === null || amountInMinorUnit === undefined || !currency || isNaN(amountInMinorUnit)) return '';

  try {
    const currencyDecimalPlaces = getCurrencyDecimalPlaces(currency, locale);
    const decimalPlaces = currencyDecimalPlaces + Math.max(0, extraDecimalPlaces);
    const divisor = Math.pow(10, currencyDecimalPlaces);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(amountInMinorUnit / divisor);
  } catch {
    const currencyDecimalPlaces = getCurrencyDecimalPlaces(currency, locale);
    const decimalPlaces = currencyDecimalPlaces + Math.max(0, extraDecimalPlaces);
    const divisor = Math.pow(10, currencyDecimalPlaces);
    return `${(amountInMinorUnit / divisor).toFixed(decimalPlaces)} ${currency.toUpperCase()}`;
  }
}

export function getCurrencySymbol(currency: string, locale: string): string {
  try {
    const parts = new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).formatToParts(
      0,
    );
    return parts.find((p) => p.type === 'currency')?.value || currency.toUpperCase();
  } catch {
    return currency.toUpperCase();
  }
}

export function isIntegerString(str: string): boolean {
  return str.trim() !== '' && !isNaN(Number(str));
}
